(() => {
  if (window.sfStorefrontReady) return;
  window.sfStorefrontReady = true;
  const config = JSON.parse(document.getElementById('sf-config')?.textContent || '{}');
  const root = (config.root || '/').replace(/\/?$/, '/');
  const dialog = document.getElementById('sf-cart-dialog');
  const live = document.getElementById('sf-live');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let opener = null;
  let queue = Promise.resolve();
  let pending = 0;
  let knownDiscountCodes = null;
  let searchController;
  let searchTimer;
  let brandingFrame;
  const announce = message => { if (live) live.textContent = message; };
  const parse = text => new DOMParser().parseFromString(text, 'text/html');
  const cartRoots = () => [...document.querySelectorAll('[data-cart-render]')];
  const sectionIds = () => [...new Set(cartRoots().map(el => el.dataset.sectionId).filter(Boolean))];
  const reportError = (error, form) => {
    const message = typeof error.message === 'string' ? error.message : config.labels;
    let target = form?.querySelector('.sf-form-error');
    if (!target) target = dialog?.open ? dialog.querySelector('.sf-cart-error') : document.querySelector('.sf-cart-page .sf-cart-error');
    if (target) { target.textContent = message || config.labels; target.hidden = false; }
    announce(message || config.labels);
  };
  function closeCart() {
    if (!dialog?.open) return;
    dialog.close();
  }
  function showCart() {
    if (!dialog?.showModal) { location.assign(config.cart || root + 'cart'); return; }
    if (!dialog.open) {
      opener = document.activeElement;
      document.querySelectorAll('.sf-header details[open]').forEach(detail => detail.removeAttribute('open'));
      dialog.showModal();
      document.body.classList.add('sf-cart-open');
      dialog.querySelector('[data-close-cart]')?.focus({ preventScroll: true });
    }
  }
  function replaceSections(sections) {
    if (!sections || typeof sections !== 'object') return false;
    let updated = false;
    for (const [id, html] of Object.entries(sections)) {
      if (!html) continue;
      const oldRoot = cartRoots().find(el => el.dataset.sectionId === id);
      const newRoot = parse(html).querySelector('[data-cart-render]');
      if (!oldRoot || !newRoot) continue;
      const openPanels = [...oldRoot.querySelectorAll('details[open]')].map(el => el.classList.contains('sf-cart-discount') ? '.sf-cart-discount' : el.classList.contains('sf-cart-note') ? '.sf-cart-note' : null).filter(Boolean);
      const draftNote = oldRoot.querySelector('[data-cart-note]');
      const unsavedNote = draftNote && draftNote.value !== draftNote.defaultValue ? draftNote.value : null;
      const scroller = oldRoot.querySelector('.sf-drawer-scroll');
      const scrollTop = scroller?.scrollTop || 0;
      const active = oldRoot.contains(document.activeElement) ? document.activeElement : null;
      const key = active?.closest('[data-line-key]')?.dataset.lineKey;
      const action = active?.hasAttribute('data-cart-quantity') ? '[data-cart-quantity]' : active?.dataset.cartDelta ? `[data-cart-delta="${active.dataset.cartDelta}"]` : null;
      oldRoot.replaceWith(newRoot);
      openPanels.forEach(selector => newRoot.querySelector(selector)?.setAttribute('open', ''));
      if (unsavedNote !== null && newRoot.querySelector('[data-cart-note]')) newRoot.querySelector('[data-cart-note]').value = unsavedNote;
      const newScroller = newRoot.querySelector('.sf-drawer-scroll');
      if (newScroller) newScroller.scrollTop = scrollTop;
      document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = newRoot.dataset.count || '0'; });
      if (key && action) newRoot.querySelector(`[data-line-key="${CSS.escape(key)}"] ${action}`)?.focus({ preventScroll: true });
      else if (active && dialog?.open) dialog.querySelector('[data-close-cart]')?.focus({ preventScroll: true });
      updated = true;
    }
    if (updated) document.dispatchEvent(new CustomEvent('sf:cart-rendered'));
    return updated;
  }
  async function refreshCart() {
    const url = new URL(location.pathname, location.origin);
    url.searchParams.set('sections', sectionIds().join(','));
    const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error(config.labels);
    return replaceSections(await response.json());
  }
  function busy(state) {
    cartRoots().forEach(el => {
      el.setAttribute('aria-busy', String(state));
      el.querySelectorAll('[data-cart-delta],[data-cart-quantity],[data-save-note],[data-apply-discount],[data-remove-discount],button[name="checkout"]').forEach(control => { control.disabled = state; });
    });
  }
  function cartMutation(path, payload, form, isAdd = false) {
    pending++;
    busy(true);
    const job = queue.then(async () => {
      if (typeof payload === 'function') payload = await payload();
      const ids = sectionIds();
      const options = { method: 'POST', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(20000) };
      if (payload instanceof FormData) {
        payload.set('sections', ids.join(','));
        payload.set('sections_url', location.pathname);
        options.body = payload;
      } else {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify({ ...payload, sections: ids, sections_url: location.pathname });
      }
      const response = await fetch(root + path, options);
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        const detail = typeof data.description === 'string' ? data.description : data.message;
        throw new Error(typeof detail === 'string' ? detail : config.labels);
      }
      const rendered = replaceSections(data.sections);
      if (!rendered) {
        try { await refreshCart(); } catch (_) { location.assign(config.cart || root + 'cart'); return data; }
      }
      announce(isAdd ? config.added : config.updated);
      if (isAdd) showCart();
      document.dispatchEvent(new CustomEvent('sf:cart-updated', { detail: { itemCount: Number(cartRoots()[0]?.dataset.count || 0) } }));
      return data;
    });
    queue = job.catch(error => { reportError(error, form); }).finally(() => { pending--; busy(pending > 0); });
    return job;
  }
  async function changeDiscount(code, remove = false) {
    if (pending) return;
    if (!code) { announce(config.discountEmpty); return; }
    const initialPanel = document.activeElement?.closest('.sf-cart-discount');
    const sectionId = initialPanel?.closest('[data-cart-render]')?.dataset.sectionId;
    try {
      const data = await cartMutation('cart/update.js', async () => {
        const response = await fetch(root + 'cart.js', {headers: {Accept: 'application/json'}, signal: AbortSignal.timeout(15000)});
        if (!response.ok) throw new Error(config.labels);
        const cart = await response.json();
        const fromDom = [...document.querySelectorAll('[data-remove-discount]')].map(el => el.dataset.removeDiscount);
        const current = Array.isArray(cart.discount_codes) ? cart.discount_codes.map(item => item.code) : knownDiscountCodes || fromDom;
        const codes = [...new Set(current.filter(item => item.toLowerCase() !== code.toLowerCase()))];
        if (!remove) codes.push(code);
        knownDiscountCodes = codes;
        return {discount: codes.join(',')};
      });
      const codes = Array.isArray(data.discount_codes) ? data.discount_codes : null;
      if (codes) knownDiscountCodes = codes.map(item => item.code);
      const matched = codes?.find(item => item.code.toLowerCase() === code.toLowerCase());
      const applications = [...(data.cart_level_discount_applications || []), ...(data.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(a => a.discount_application))];
      const applied = matched?.applicable === true || applications.some(item => item.type === 'discount_code' && item.title.toLowerCase() === code.toLowerCase());
      const message = remove ? config.discountRemoved : applied ? config.discountApplied : matched?.applicable === false ? config.discountRejected : config.discountSaved;
      document.querySelectorAll('.sf-cart-discount').forEach(panel => {
        const list = panel.querySelector('[data-discount-codes]');
        list.replaceChildren();
        (knownDiscountCodes || []).forEach(value => {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'sf-discount-chip'; button.dataset.removeDiscount = value; button.textContent = value + ' ×'; button.setAttribute('aria-label', 'Remove code ' + value); list.append(button);
        });
        panel.querySelector('.sf-discount-status').textContent = message;
        if (panel.closest('[data-cart-render]')?.dataset.sectionId === sectionId) {
          panel.open = true;
          const input = panel.querySelector('[data-discount-input]'); if (!applied && !remove) input.value = code;
          input.focus({preventScroll: true});
        }
      });
      announce(message);
    } catch (_) {}
  }
  function resolveCartLine(items, key) {
    if (!Array.isArray(items) || !key) return 0;
    const exact = items.findIndex(item => item.key === key);
    if (exact >= 0) return exact + 1;
    const variantId = String(key).split(':')[0];
    if (!/^\d+$/.test(variantId)) return 0;
    const matches = items.map((item, index) => ({ item, index })).filter(({ item }) => String(item.variant_id) === variantId);
    return matches.length === 1 ? matches[0].index + 1 : 0;
  }
  function updateLine(line, value) {
    if (!line || pending) return;
    const input = line.querySelector('[data-cart-quantity]');
    const original = Number(input.defaultValue);
    const quantity = Number(value);
    if (!Number.isInteger(quantity) || quantity < 0) { input.value = original; return; }
    input.value = quantity;
    const oldKey = line.dataset.lineKey;
    cartMutation('cart/change.js', async () => {
      const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error(config.labels);
      const current = await response.json();
      const position = resolveCartLine(current.items, oldKey);
      if (!position) { await refreshCart(); throw new Error(config.labels); }
      // Discount and shipping recalculation can change line keys. Resolve the
      // current one-based position before changing this specific cart line.
      return { line: position, quantity };
    }).catch(() => { if (input.isConnected) input.value = original; });
  }
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-discount-input]')) { event.preventDefault(); changeDiscount(event.target.value.trim()); }
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (form.matches('.sf-add-form')) {
      event.preventDefault();
      if (form.matches('.sf-main-add-form')) {
        const container = form.closest('[data-product-root]');
        const data = JSON.parse(container.querySelector('[data-variant-data]').textContent);
        const variant = data.variants.find(item => String(item.id) === String(new FormData(form).get('id')));
        if (!variant || !variant.available) {
          container.querySelector('[data-main-add][data-ss-restock-open]')?.click();
          return;
        }
      }
      if (form.dataset.busy || !form.reportValidity()) return;
      const button = event.submitter || form.querySelector('[type=submit]');
      const markup = button.innerHTML;
      const wasDisabled = button.disabled;
      form.dataset.busy = 'true'; button.disabled = true; button.textContent = config.adding;
      const error = form.querySelector('.sf-form-error');
      if (error) error.hidden = true;
      const formData = new FormData(form);
      const payload = form.matches('[data-trio-form]') ? { items: [0, 1, 2].map(index => ({ id: formData.get(`items[${index}][id]`), quantity: 1 })) } : formData;
      const addPromise = cartMutation('cart/add.js', payload, form, true);
      if (event.agentInvoked && typeof event.respondWith === 'function') event.respondWith(addPromise.then(data => ({success: true, message: config.added, cartUrl: config.cart})).catch(error => ({success: false, message: error.message})));
      try { await addPromise; }
      catch (_) {}
      finally { delete form.dataset.busy; button.innerHTML = markup; button.disabled = wasDisabled; }
    } else if (event.submitter?.name === 'checkout' && form.closest('[data-cart-render]')) {
      if (pending) { event.preventDefault(); return; }
      const note = form.closest('[data-cart-render]')?.querySelector('[data-cart-note]');
      if (note && !form.querySelector('[name=note]')) {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = 'note'; input.value = note.value; form.append(input);
      }
    }
  });
  document.addEventListener('click', event => {
    const open = event.target.closest('[data-open-cart]');
    if (open && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault(); showCart();
      if (!pending) refreshCart().catch(error => reportError(error));
      return;
    }
    if (event.target.closest('[data-close-cart]')) { closeCart(); return; }
    const delta = event.target.closest('[data-cart-delta]');
    if (delta) {
      const line = delta.closest('[data-line-key]');
      const input = line.querySelector('[data-cart-quantity]');
      updateLine(line, Math.max(0, Number(input.value) + Number(delta.dataset.cartDelta) * Number(input.step || 1)));
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); if (!pending) updateLine(remove.closest('[data-line-key]'), 0); return; }
    const quantity = event.target.closest('[data-quantity-delta]');
    if (quantity) {
      const input = quantity.parentElement.querySelector('input');
      const next = Number(input.value || input.min || 1) + Number(quantity.dataset.quantityDelta) * Number(input.step || 1);
      input.value = Math.max(Number(input.min || 1), Math.min(input.max ? Number(input.max) : Infinity, next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const applyCode = event.target.closest('[data-apply-discount]');
    if (applyCode) { changeDiscount(applyCode.closest('.sf-cart-discount').querySelector('[data-discount-input]').value.trim()); return; }
    const removeCode = event.target.closest('[data-remove-discount]');
    if (removeCode) { changeDiscount(removeCode.dataset.removeDiscount, true); return; }
    const noteButton = event.target.closest('[data-save-note]');
    if (noteButton && !pending) {
      const note = noteButton.closest('.sf-cart-note').querySelector('[data-cart-note]').value;
      cartMutation('cart/update.js', { note }).then(() => {
        document.querySelectorAll('.sf-note-status').forEach(el => { el.textContent = config.noteSaved; });
        announce(config.noteSaved);
      }).catch(() => {});
      return;
    }
    const mediaButton = event.target.closest('[data-media-target]');
    if (mediaButton) selectMedia(mediaButton.closest('[data-product-root]'), mediaButton.dataset.mediaTarget);
    const zoom = event.target.closest('[data-image-zoom]');
    if (zoom && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      let modal = document.querySelector('.sf-image-dialog');
      if (!modal) {
        modal = document.createElement('dialog'); modal.className = 'sf-image-dialog';
        const close = document.createElement('button'); close.type = 'button'; close.className = 'sf-icon-button'; close.textContent = '×'; close.setAttribute('aria-label', dialog?.querySelector('[data-close-cart]')?.getAttribute('aria-label') || 'Close');
        close.addEventListener('click', () => modal.close());
        const image = document.createElement('img'); modal.append(close, image); document.body.append(modal);
        modal.addEventListener('click', e => { if (e.target === modal) modal.close(); });
      }
      const image = modal.querySelector('img'); image.src = zoom.href; image.alt = zoom.querySelector('img')?.alt || '';
      modal.showModal();
    }
    const press = event.target.closest('[data-press-toggle]');
    if (press) {
      const container = press.closest('.sf-press');
      const paused = container.classList.toggle('is-paused');
      press.setAttribute('aria-pressed', String(paused)); press.textContent = paused ? config.resume : config.pause;
    }
    if (event.target.closest('[data-motion-toggle]')) setMotion(!document.documentElement.classList.contains('sf-motion-paused'), true);
    if (!event.target.closest('.sf-header details')) document.querySelectorAll('.sf-header details[open]').forEach(detail => detail.removeAttribute('open'));
    document.querySelectorAll('.sf-filter[open]').forEach(detail => { if (!detail.contains(event.target)) detail.removeAttribute('open'); });
  });
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-cart-quantity]')) updateLine(input.closest('[data-line-key]'), input.value);
    if (input.matches('[data-variant-selector]')) updateVariant(input.closest('[data-product-root]'), input.value);
  });
  dialog?.addEventListener('close', () => {
    document.body.classList.remove('sf-cart-open');
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });
  dialog?.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeCart();
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      const open = [...document.querySelectorAll('.sf-header details[open],.sf-filter[open]')];
      open.forEach(detail => detail.removeAttribute('open'));
      open[0]?.querySelector('summary')?.focus();
    }
  });
  document.querySelectorAll('.sf-header details').forEach(detail => detail.addEventListener('toggle', () => {
    if (!detail.open) return;
    document.querySelectorAll('.sf-header details[open]').forEach(other => { if (other !== detail && !other.contains(detail) && !detail.contains(other)) other.removeAttribute('open'); });
    if (detail.classList.contains('sf-search-menu')) detail.querySelector('input[type=search]')?.focus();
  }));
  function selectMedia(container, id) {
    if (!container || !id) return;
    container.querySelectorAll('[data-media-id]').forEach(media => {
      const active = media.dataset.mediaId === String(id);
      media.hidden = !active; media.classList.toggle('is-active', active);
      if (!active) media.querySelector('video')?.pause();
    });
    container.querySelectorAll('[data-media-target]').forEach(button => {
      const active = button.dataset.mediaTarget === String(id);
      button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active));
    });
  }
  function updateVariant(container, id) {
    if (!container) return;
    const data = JSON.parse(container.querySelector('[data-variant-data]').textContent);
    const variant = data.variants.find(item => String(item.id) === String(id));
    if (!variant) return;
    const price = container.querySelector('[data-variant-price]');
    price.replaceChildren();
    const current = document.createElement('span'); current.className = 'sf-price' + (variant.compare ? ' sf-price--sale' : ''); current.textContent = variant.price; price.append(current);
    if (variant.compare) { const previous = document.createElement('s'); previous.className = 'sf-compare'; previous.textContent = variant.compare; price.append(previous); }
    container.querySelector('[data-sticky-price]').textContent = variant.price;
    container.querySelector('[data-variant-stock]').textContent = variant.lowStock || (variant.available ? data.inStock : data.outStock);
    const shippingNotice = container.querySelector('[data-small-order-shipping]');
    if (shippingNotice) shippingNotice.hidden = !variant.smallOrderShipping;
    const trioNote = container.querySelector('[data-product-trio]');
    if (trioNote) trioNote.hidden = !variant.trioEligible;
    container.querySelector('[data-variant-sku]').textContent = variant.sku || '';
    container.querySelector('[data-sku-row]').hidden = !variant.sku;
    container.querySelectorAll('[data-main-add],[data-sticky-add]').forEach(button => {
      button.disabled = false;
      button.type = variant.available ? 'submit' : 'button';
      button.classList.toggle('ss-notify-button', !variant.available);
      button.toggleAttribute('data-ss-restock-open', !variant.available);
      if (variant.available) {
        button.removeAttribute('aria-haspopup');
        button.removeAttribute('aria-controls');
      } else {
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', button.dataset.restockTarget);
      }
    });
    container.querySelector('[data-add-label]').textContent = variant.available ? data.add : data.soldOut;
    container.querySelector('[data-sticky-add]').textContent = variant.available ? data.add : data.soldOut;
    const quantity = container.querySelector('.sf-main-add-form [name=quantity]');
    quantity.min = variant.min; quantity.step = variant.step;
    if (variant.max) quantity.max = variant.max; else quantity.removeAttribute('max');
    quantity.value = Math.max(variant.min, Math.min(variant.max || Infinity, Math.ceil(Number(quantity.value) / variant.step) * variant.step));
    if (variant.media) selectMedia(container, variant.media);
    const url = new URL(location.href); url.searchParams.set('variant', String(id)); history.replaceState({}, '', url);
    container.dispatchEvent(new CustomEvent('sf:variant-updated', { detail: { variant } }));
  }
  function initProducts() {
    document.querySelectorAll('[data-product-root]:not([data-sf-initialized])').forEach(container => {
      container.dataset.sfInitialized = 'true';
      const primary = container.querySelector('.sf-main-add-form');
      const sticky = container.querySelector('[data-sticky-buy]');
      if (!primary || !sticky) return;
      const observer = new IntersectionObserver(entries => {
        const entry = entries[0]; sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
      }, { threshold: 0, rootMargin: '-76px 0px 0px 0px' });
      observer.observe(primary);
      container._sfObserver = observer;
    });
  }
  function updateBrandVisibility() {
    brandingFrame = null;
    const headerBottom = document.querySelector('[data-sf-header]')?.getBoundingClientRect().bottom || 64;
    const panel = document.querySelector('.ss-catalogue');
    const panelTop = panel?.getBoundingClientRect().top ?? Infinity;
    const visible = [...document.querySelectorAll('[data-section-brand]')].some(logo => {
      const rect = logo.getBoundingClientRect();
      const maxBottom = logo.closest('ss-incense-portal') ? Math.min(innerHeight, panelTop) : innerHeight;
      return rect.width > 0 && rect.height > 0 && rect.bottom > headerBottom + 8 && rect.top < maxBottom - 12 && rect.right > 0 && rect.left < innerWidth;
    });
    document.documentElement.classList.toggle('sf-section-brand-visible', visible);
  }
  function scheduleBranding() { if (!brandingFrame) brandingFrame = requestAnimationFrame(updateBrandVisibility); }
  document.addEventListener('scroll', scheduleBranding, { passive: true });
  window.addEventListener('resize', scheduleBranding, { passive: true });
  const brandObserver = new IntersectionObserver(scheduleBranding, { threshold: [0, .2, .5, 1] });
  document.querySelectorAll('[data-section-brand],.ss-catalogue').forEach(el => brandObserver.observe(el));
  document.addEventListener('input', event => {
    if (!event.target.matches('[data-sf-search]')) return;
    clearTimeout(searchTimer); searchController?.abort();
    const input = event.target; const results = input.closest('.sf-search-panel').querySelector('[data-predictive-results]');
    const term = input.value.trim();
    results.replaceChildren();
    if (term.length < 2) return;
    searchTimer = setTimeout(async () => {
      searchController = new AbortController();
      const url = new URL(config.search || root + 'search/suggest', location.origin);
      url.searchParams.set('q', term); url.searchParams.set('resources[type]', 'product'); url.searchParams.set('resources[limit]', '5'); url.searchParams.set('section_id', 'sf-predictive-search');
      try {
        const response = await fetch(url, { signal: searchController.signal });
        if (!response.ok) return;
        const fragment = parse(await response.text()).querySelector('[data-predictive-root]');
        if (input.value.trim() === term && fragment) results.replaceChildren(fragment);
      } catch (_) {}
    }, 200);
  });
  const recommendationObserver = new IntersectionObserver(entries => entries.forEach(async entry => {
    if (!entry.isIntersecting) return;
    const section = entry.target; recommendationObserver.unobserve(section);
    try {
      const response = await fetch(section.dataset.url, { signal: AbortSignal.timeout(12000) });
      if (!response.ok) return;
      const replacement = parse(await response.text()).querySelector('[data-recommendations]');
      if (replacement?.querySelector('.sf-card')) section.replaceWith(replacement);
    } catch (_) {}
  }), { rootMargin: '250px' });
  document.querySelectorAll('[data-recommendations]').forEach(el => recommendationObserver.observe(el));
  function setMotion(paused, persist = false) {
    const state = typeof paused === 'boolean' ? paused : reduced.matches;
    document.documentElement.classList.toggle('sf-motion-paused', state);
    document.documentElement.classList.toggle('sf-motion-enabled', !state);
    document.documentElement.dataset.sfMotion = state ? 'paused' : 'running';
    document.querySelectorAll('[data-motion-toggle]').forEach(button => { button.setAttribute('aria-pressed', String(state)); button.textContent = state ? config.resume : config.pause; });
    document.dispatchEvent(new CustomEvent('sf:motion', { detail: { paused: state } }));
    if (persist) { savedMotion = paused; try { localStorage.setItem('sf-motion-paused', String(paused)); } catch (_) {} }
  }
  let savedMotion = null;
  try {
    const stored = localStorage.getItem('sf-motion-paused');
    savedMotion = stored === 'true' ? true : stored === 'false' ? false : null;
  } catch (_) {}
  setMotion(savedMotion);
  const syncMotion = () => setMotion(savedMotion);
  if (reduced.addEventListener) reduced.addEventListener('change', syncMotion);
  else reduced.addListener(syncMotion);
  window.addEventListener('pageshow', syncMotion);
  document.addEventListener('shopify:section:load', () => { initProducts(); scheduleBranding(); });
  document.addEventListener('shopify:section:unload', event => { event.target.querySelectorAll('[data-product-root]').forEach(el => el._sfObserver?.disconnect()); });
  function updateAddressCountry(select, initial = false) {
    const form = select.closest('form');
    const province = form.querySelector('[data-address-province]');
    if (initial && select.dataset.selected) select.value = select.dataset.selected;
    let options = [];
    try { options = JSON.parse(select.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) {}
    province.replaceChildren(...options.map(([value, name]) => { const option = document.createElement('option'); option.value = value; option.textContent = name; return option; }));
    form.querySelector('[data-province-label]').hidden = options.length === 0;
    province.disabled = options.length === 0;
    if (initial && province.dataset.selected) province.value = province.dataset.selected;
  }
  document.querySelectorAll('[data-address-country]').forEach(select => { updateAddressCountry(select, true); select.addEventListener('change', () => updateAddressCountry(select)); });
  if (location.hash === '#recover' || document.querySelector('#recover .errors,#recover [role=status]')) document.getElementById('recover')?.setAttribute('open', '');
  initProducts(); scheduleBranding();

  function trioIllustration(current, picks) {
    const prices = [current, ...picks];
    if (prices.length !== 3 || prices.some(price => !Number.isSafeInteger(price) || price <= 4800)) return null;
    const total = prices.reduce((sum, price) => sum + price, 0);
    return { total: total - 3000, prices: prices.map(price => price - Math.round(3000 * price / total)) };
  }

  class ProductTrio extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.root = this.closest('[data-product-root]');
      const signal = this.controller.signal;
      this.variant = { priceCents: Number(this.dataset.price), trioEligible: this.dataset.eligible === 'true', available: this.dataset.eligible === 'true' };
      this.root?.addEventListener('sf:variant-updated', event => { this.variant = event.detail.variant; this.render(); }, { signal });
      this.root?.addEventListener('change', event => { if (event.target.matches('[name=selling_plan]')) this.render(); }, { signal });
      document.addEventListener('sf:cart-updated', () => this.render(), { signal });
      document.addEventListener('sf:cart-rendered', () => this.render(), { signal });
      window.addEventListener('pageshow', () => this.render(), { signal });
      this.render();
    }
    render() {
      this.hidden = !this.variant.trioEligible || !this.variant.available || Boolean(this.root?.querySelector('.sf-main-add-form [name=selling_plan]')?.value);
      if (this.hidden) return;
      const source = document.querySelector('[data-cart-render] [data-trio-count]');
      const count = Number(source?.dataset.trioCount || 0);
      const applied = source?.dataset.trioApplied === 'true';
      const progress = this.querySelector('[data-trio-count]');
      if (progress) {
        progress.dataset.trioCount = String(count); progress.dataset.trioApplied = String(applied);
        const meter = Math.min(3, Math.max(0, count));
        const bar = progress.querySelector('progress');
        bar.value = meter;
        bar.textContent = meter + '/3';
        progress.querySelector('.sf-meter-heading > span').textContent = meter + '/3';
        progress.querySelector('.sf-meter-heading > strong').textContent = applied ? this.dataset.appliedLabel : count >= 3 ? this.dataset.readyLabel : this.dataset.remainingLabel.replace('[count]', String(3 - count));
      }
      const cards = [...this.querySelectorAll('[data-trio-pick]')];
      const example = trioIllustration(Number(this.variant.priceCents), cards.map(card => Number(card.dataset.price)));
      if (!example) return;
      const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol' }).format(value / 100);
      cards.forEach((card, index) => {
        const price = card.querySelector('[data-trio-example-price]');
        if (price) price.textContent = this.dataset.priceLabel.replace('[price]', money(example.prices[index + 1]));
      });
      const total = this.querySelector('[data-trio-example-total]');
      if (total) total.textContent = this.dataset.totalLabel.replace('[price]', money(example.total));
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  if (!customElements.get('sf-product-trio')) customElements.define('sf-product-trio', ProductTrio);
})();

